import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { eventsApi } from '../api/events';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { ApiError } from '../api/client';
import { SHORT_CODE_REGEX, normalizeCode } from '../utils/shortCode';
import { joinCache, type WizardCache } from '../utils/joinCache';
import type { EventByCode } from '../api/types';

type Step = 'code' | 'team' | 'pick-slot' | 'identity';

export function EventJoin() {
  const params = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { actor, refresh } = useAuth();

  const initialCode = params.code ? normalizeCode(params.code) : '';
  const [code, setCode] = useState(initialCode);
  const [preview, setPreview] = useState<EventByCode | null>(null);
  const [step, setStep] = useState<Step>('code');
  const [teamId, setTeamId] = useState<string | undefined>();
  const [createTeamName, setCreateTeamName] = useState<string>('');
  const [displayName, setDisplayName] = useState('');
  const [handicap, setHandicap] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [slotSearch, setSlotSearch] = useState('');
  const [claimSlotId, setClaimSlotId] = useState<string | null>(null);

  // Restore wizard cache on code lock-in
  useEffect(() => {
    if (!SHORT_CODE_REGEX.test(code)) {
      setPreview(null);
      return;
    }
    let cancel = false;
    eventsApi
      .byCode(code)
      .then((p) => {
        if (cancel) return;
        setPreview(p);
        setErr(null);
        const cached = joinCache.getWizard(code);
        if (cached) {
          // Drop stale teamId if team no longer exists.
          const stillExists = cached.teamId
            ? p.teams.some((t) => t.id === cached.teamId)
            : true;
          const next: WizardCache = {
            step: cached.step,
            teamId: stillExists ? cached.teamId : undefined,
            createTeamName: stillExists ? cached.createTeamName : undefined,
            displayName: cached.displayName,
            handicap: cached.handicap ?? undefined,
          };
          setTeamId(next.teamId);
          setCreateTeamName(next.createTeamName ?? '');
          setDisplayName(next.displayName ?? '');
          setHandicap(next.handicap != null ? String(next.handicap) : '');
          if (p.format === 'best_ball_4some') {
            setStep(next.step);
          } else {
            setStep(p.unclaimedSlots.length > 0 ? 'pick-slot' : 'identity');
          }
        } else {
          if (p.format === 'best_ball_4some') {
            setStep('team');
          } else {
            setStep(p.unclaimedSlots.length > 0 ? 'pick-slot' : 'identity');
          }
        }
      })
      .catch((e: unknown) => {
        if (!cancel) {
          setPreview(null);
          setErr(e instanceof ApiError && e.status === 404 ? 'No event with that code' : null);
        }
      });
    return () => {
      cancel = true;
    };
  }, [code]);

  // Persist wizard state whenever any field changes.
  useEffect(() => {
    if (!preview || !SHORT_CODE_REGEX.test(code)) return;
    if (step === 'code') return;
    joinCache.setWizard(code, {
      step: step === 'identity' ? 'identity' : 'team',
      teamId,
      createTeamName: createTeamName || undefined,
      displayName: displayName || undefined,
      handicap: handicap ? Number(handicap) : null,
    });
  }, [code, preview, step, teamId, createTeamName, displayName, handicap]);

  const codeValid = SHORT_CODE_REGEX.test(code);
  const isGuest = actor?.kind !== 'user';
  const isBestBall = preview?.format === 'best_ball_4some';

  const pickTeam = (id: string) => {
    setTeamId(id);
    setCreateTeamName('');
    const slotsOnTeam = preview?.unclaimedSlots.filter((s) => s.teamId === id) ?? [];
    setStep(slotsOnTeam.length > 0 ? 'pick-slot' : 'identity');
  };

  const proposeCreateTeam = () => {
    const t = createTeamName.trim();
    if (!t) return;
    setTeamId(undefined);
    setStep('identity');
  };

  const summary = (() => {
    if (!preview) return '';
    if (!isBestBall) return `Joining ${preview.name}`;
    if (teamId) {
      const t = preview.teams.find((x) => x.id === teamId);
      return t ? `Joining team "${t.name}"` : 'Joining';
    }
    if (createTeamName) return `Creating and joining team "${createTeamName.trim()}"`;
    return 'Joining';
  })();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isGuest && !displayName.trim()) {
      setErr('Your name is required to join');
      return;
    }
    setConfirmOpen(true);
  };

  const doClaimSlot = async () => {
    if (!preview || !claimSlotId) return;
    setErr(null);
    setSubmitting(true);
    try {
      const res = await eventsApi.claimSlot(preview.id, claimSlotId);
      await refresh();
      joinCache.setJoined(preview.id, {
        participantId: res.participantId,
        teamId: res.teamId ?? undefined,
      });
      joinCache.clearWizard(code);
      setClaimSlotId(null);
      navigate(`/e/${preview.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not claim slot');
    } finally {
      setSubmitting(false);
    }
  };

  const doJoin = async () => {
    setConfirmOpen(false);
    setErr(null);
    setSubmitting(true);
    try {
      const res = await eventsApi.join({
        code,
        displayName: isGuest ? displayName.trim() : undefined,
        handicap: handicap ? Number(handicap) : undefined,
        teamId,
        createTeamName: createTeamName.trim() || undefined,
      });
      await refresh();
      joinCache.setJoined(res.event.id, {
        participantId: res.participant.id,
        teamId: res.team?.id,
      });
      joinCache.clearWizard(code);
      navigate(`/e/${res.event.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not join event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <div className="card stack">
        <h1>Join an event</h1>
        {err && <div className="error">{err}</div>}

        <div>
          <label htmlFor="code">Event code</label>
          <input
            id="code"
            value={code}
            onChange={(e) => {
              setCode(normalizeCode(e.target.value));
              setStep('code');
              setTeamId(undefined);
              setCreateTeamName('');
            }}
            placeholder="MASTRS"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={6}
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.2em', textAlign: 'center', fontSize: 22 }}
          />
        </div>

        {preview && (
          <div className="card" style={{ background: 'var(--color-green-100)' }}>
            <strong>{preview.name}</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              {preview.format === 'best_ball_4some' ? 'Best Ball' : 'Stroke Play'} · {preview.status}
            </div>
          </div>
        )}

        {preview && isBestBall && step === 'team' && (
          <div className="stack">
            <h3 style={{ margin: 0 }}>Pick a team</h3>
            {preview.teams.length === 0 && <p className="muted">No teams exist yet.</p>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {preview.teams.map((t) => {
                const pendingInTeam = preview.unclaimedSlots.filter((s) => s.teamId === t.id).length;
                const hasRoom = t.memberCount < 4;
                const selectable = hasRoom || pendingInTeam > 0;
                return (
                  <div key={t.id} className="card" style={{ padding: 12 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <strong>{t.name}</strong>
                      <span className="muted" style={{ fontSize: 12 }}>
                        {t.memberCount}/4{pendingInTeam > 0 ? ` · ${pendingInTeam} pending` : ''}
                      </span>
                    </div>
                    <Button
                      block
                      variant="secondary"
                      disabled={!selectable || preview.status === 'final'}
                      onClick={() => pickTeam(t.id)}
                    >
                      Select
                    </Button>
                  </div>
                );
              })}
            </div>
            {preview.allowGuestTeams && preview.status !== 'final' && (
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  proposeCreateTeam();
                }}
                style={{ gap: 8 }}
              >
                <input
                  value={createTeamName}
                  onChange={(e) => setCreateTeamName(e.target.value)}
                  placeholder="Or create a new team"
                  maxLength={60}
                />
                <Button type="submit" disabled={!createTeamName.trim()}>+ Create</Button>
              </form>
            )}
          </div>
        )}

        {preview && step === 'pick-slot' && (
          <div className="stack">
            <h3 style={{ margin: 0 }}>Pick which player you are</h3>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              The admin pre-loaded the roster. Find your name below, or join as a new player.
            </p>
            <input
              value={slotSearch}
              onChange={(e) => setSlotSearch(e.target.value)}
              placeholder="Search by name…"
              aria-label="Search players"
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {preview.unclaimedSlots
                .filter((s) => (isBestBall ? s.teamId === teamId : true))
                .filter((s) =>
                  slotSearch.trim()
                    ? s.displayName.toLowerCase().includes(slotSearch.trim().toLowerCase())
                    : true,
                )
                .map((s) => (
                  <div key={s.id} className="card" style={{ padding: 12 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <strong>{s.displayName}</strong>
                      {s.handicap != null && (
                        <span className="muted" style={{ fontSize: 12 }}>hcp {s.handicap}</span>
                      )}
                    </div>
                    <Button
                      block
                      variant="secondary"
                      disabled={preview.status === 'final'}
                      onClick={() => setClaimSlotId(s.id)}
                    >
                      I am {s.displayName}
                    </Button>
                  </div>
                ))}
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {isBestBall && (
                <Button variant="secondary" onClick={() => setStep('team')}>
                  ← Back to teams
                </Button>
              )}
              {(() => {
                const pickedTeam = isBestBall ? preview.teams.find((t) => t.id === teamId) : null;
                const teamFull = isBestBall && pickedTeam ? pickedTeam.memberCount >= 4 : false;
                if (teamFull) return null;
                return (
                  <Button variant="secondary" onClick={() => setStep('identity')}>
                    Join as a new player instead
                  </Button>
                );
              })()}
            </div>
          </div>
        )}

        {preview && step === 'identity' && (
          <form onSubmit={onSubmit} className="stack">
            {isBestBall && (
              <div className="muted" style={{ fontSize: 13 }}>
                {summary}.{' '}
                <button
                  type="button"
                  onClick={() => setStep('team')}
                  style={{ background: 'none', border: 'none', color: 'var(--fg-primary)', cursor: 'pointer', padding: 0 }}
                >
                  Change team
                </button>
              </div>
            )}
            {isGuest && (
              <div>
                <label htmlFor="dn">Your name</label>
                <input
                  id="dn"
                  required
                  minLength={1}
                  maxLength={60}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="How you'll appear on the scorecard"
                />
              </div>
            )}
            <div>
              <label htmlFor="hcap">Handicap (optional)</label>
              <input
                id="hcap"
                type="number"
                min={0}
                max={54}
                step="0.1"
                value={handicap}
                onChange={(e) => setHandicap(e.target.value)}
                placeholder="e.g. 14.2"
              />
            </div>
            <Button type="submit" block disabled={submitting || !codeValid}>
              {submitting ? 'Joining…' : 'Join event'}
            </Button>
          </form>
        )}
      </div>

      <Modal
        open={confirmOpen}
        title="Confirm join"
        body={<p style={{ margin: 0 }}>{summary}. Continue?</p>}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={doJoin} disabled={submitting}>Join</Button>
          </>
        }
        onClose={() => setConfirmOpen(false)}
      />

      <Modal
        open={!!claimSlotId}
        title="Confirm your player"
        body={
          <p style={{ margin: 0 }}>
            You are about to join as{' '}
            <strong>
              {preview?.unclaimedSlots.find((s) => s.id === claimSlotId)?.displayName ?? ''}
            </strong>
            . This cannot be changed by you — only the admin can rename players. Continue?
          </p>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setClaimSlotId(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={doClaimSlot} disabled={submitting}>
              Yes, that's me
            </Button>
          </>
        }
        onClose={() => setClaimSlotId(null)}
      />
    </div>
  );
}
